import {
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  FormInstance,
  Row,
  Select,
  Typography,
} from "antd";

import { useEffect } from "react";
import "./ProjectInstallationInformation.scss";
import { useBoundStore } from "store";
import { ProjectFormFieldItem } from "../project-form-field-item/ProjectFormFieldItem";
import { isEmpty } from "lodash";
import dayjs from "dayjs";
import { useDisplayFieldByStage } from "projects/hooks";
import { DateFormat } from "shared/constants";
import { MoneyInput } from "shared/components";
import { useSubcontractors } from "subcontractors/queries";

type Props = {
  form: FormInstance;
  currentStage: string;
  isEditing: boolean;
};

const fieldCategory = "installationInformationFields";

export const ProjectInstallationInformation = ({
  form,
  currentStage,
  isEditing,
}: Props) => {
  const {
    lightingSubcontractorsSelectOptions,
    hvacSubcontractorsSelectOptions,
    refrigerationSubcontractorsSelectOptions,
    setSubcontractorsSelectOptions,
  } = useBoundStore();

  // Ensure subcontractor options are loaded (they may not be if navigating directly to project detail)
  const { data: subcontractorsData } = useSubcontractors();
  useEffect(() => {
    if (subcontractorsData) {
      setSubcontractorsSelectOptions(subcontractorsData);
    }
  }, [subcontractorsData, setSubcontractorsSelectOptions]);

  const validationStage = Form.useWatch("stage", form);
  const hasStageChanged = form.isFieldTouched("stage");
  const { displayFieldCategoryByStage } = useDisplayFieldByStage();
  const selectPlaceholder = isEditing ? "Select..." : "None";
  const datePlaceholder = isEditing ? "Select date" : "None";
  const moneyPlaceholder = isEditing ? undefined : "None";

  return displayFieldCategoryByStage(fieldCategory, currentStage) ? (
    <div className='project-installation-information-container'>
      <Flex>
        <Typography.Title
          className='project-installation-information-title'
          level={2}
        >
          Installation Information
        </Typography.Title>
      </Flex>
      <Divider className='project-installation-information-divider' />
      <Row justify='start' gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <ProjectFormFieldItem
          name='lightingSubcontractorId'
          label='Lighting Sub'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Select
            size='small'
            options={[
              { value: "", label: selectPlaceholder },
              ...lightingSubcontractorsSelectOptions,
            ]}
            onSelect={(value) => {
              form.setFieldValue("lightingSubcontractorId", value);
            }}
            placeholder={selectPlaceholder}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='hvacSubcontractorId'
          label='HVAC Sub'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Select
            size='small'
            options={[
              { value: "", label: selectPlaceholder },
              ...hvacSubcontractorsSelectOptions,
            ]}
            onSelect={(value) => {
              form.setFieldValue("hvacSubcontractorId", value);
            }}
            placeholder={selectPlaceholder}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='refrigerationSubcontractorId'
          label='Ref Sub'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Select
            size='small'
            options={[
              { value: "", label: selectPlaceholder },
              ...refrigerationSubcontractorsSelectOptions,
            ]}
            onSelect={(value) => {
              form.setFieldValue("refrigerationSubcontractorId", value);
            }}
            placeholder={selectPlaceholder}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='customerWelcomeLetterSent'
          label='Customer Welcome Letter Sent'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "customerWelcomeLetterSent",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='mechanicalPOSent'
          label='Mechanical PO Sent'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "mechanicalPOSent",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='lightingLaborPOSent'
          label='Ltg. Labor PO Sent'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "lightingLaborPOSent",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='lightingMaterialPOSent'
          label='Ltg. Material PO Sent'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "lightingMaterialPOSent",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='refrigerationPOSent'
          label='Ref PO Sent'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "refrigerationPOSent",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='refrigerationETA'
          label='Refrigeration ETA'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "refrigerationETA",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='hvacETA'
          label='HVAC ETA'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "hvacETA",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='lightingETA'
          label='Ltg. ETA'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "lightingETA",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='refrigerationInstalled'
          label='Ref Installed'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "refrigerationInstalled",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='hvacInstalled'
          label='HVAC Installed'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "hvacInstalled",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='lightingInstalled'
          label='Ltg. Installed'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "lightingInstalled",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='lightingMaterialPOAmount'
          label='Ltg Material PO Amount'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <MoneyInput size='small' placeholder={moneyPlaceholder} />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='lightingLaborPOAmount'
          label='Ltg Labor PO Amount'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <MoneyInput size='small' placeholder={moneyPlaceholder} />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='mechanicalPOAmount'
          label='Mechanical PO Amount'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <MoneyInput size='small' placeholder={moneyPlaceholder} />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='refrigerationPOAmount'
          label='Refrigeration PO Amount'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <MoneyInput size='small' placeholder={moneyPlaceholder} />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='completionDocumentSentToCustomer'
          label='Completion Document Sent to Customer'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "completionDocumentSentToCustomer",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='closeoutPackageSubmittedToUtility'
          label='Closeout Package Submitted to Utility'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "closeoutPackageSubmittedToUtility",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='totalProjectInstalledDate'
          label='Total Project Installed'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "totalProjectInstalledDate",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='totalProjectCompletedDate'
          label='Total Project Completed'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "totalProjectCompletedDate",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='closeoutPackageReceived'
          label='Closeout Package Received'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "closeoutPackageReceived",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='closeoutPackageReviewed'
          label='Closeout Package Reviewed'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "closeoutPackageReviewed",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='utilityPostInspectionScheduleDate'
          label='Utility Post Inspection Schedule Date'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "utilityPostInspectionScheduleDate",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='utilityPostInspectionCompleteDate'
          label='Utility Post Inspection Complete Date'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "utilityPostInspectionCompleteDate",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='utilityEcd'
          label='Utility ECD'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "utilityEcd",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
      </Row>
    </div>
  ) : null;
};
